import delegate from "delegate-it";
import ajax from "@/scripts/common/ajax";
import { createValidator } from "./validation/validator";
import { toggleSubmit, showFormMessages } from "@/scripts/components/form/form";
import { fireEvent } from "@/scripts/common/util";
import { scrollToIfNeeded } from "@/scripts/common/scroll-to";
import "@/styles/components/multistep-form.css";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";

const handleMessages = (form, validator, messages, success) => {
  const formMessages = [];

  messages.forEach((message) => {
    if (message.fieldName) {
      validator.addFieldError(form.elements[message.fieldName], message.message);
    } else {
      formMessages.push(message);
    }
  });

  showFormMessages(form, formMessages, success);
};

const handleNextStep = async (form, validator) => {
  const currentProgressBar = document.querySelector("[data-multistep-progress-bar]");
  const currentSection = document.querySelector("[data-multistep-content]");
  currentSection?.classList.add("is-loading");

  const formData = new FormData(form);

  try {
    const req = await ajax.post(form.action, { body: formData });
    const contentType = req.headers.get("content-type");
    const res = contentType.includes("application/json") ? await req.json() : await req.text();

    if (typeof res === "string") {
      const range = document.createRange();
      const fragment = range.createContextualFragment(res);
      const newSection = fragment.querySelector("[data-multistep-content]");
      currentSection.replaceWith(newSection);

      const updatedUrl = form.dataset.nextStepUrl;

      if (updatedUrl) {
        window.history.pushState({ url: updatedUrl }, "", updatedUrl);
      }

      const newProgressBar = fragment.querySelector("[data-multistep-progress-bar]");

      const nextProgressStep = currentProgressBar.querySelector(".is-current + .progress-step");

      let timeout = 0;

      if (nextProgressStep && !nextProgressStep.classList.contains("is-visited")) {
        nextProgressStep.classList.add("is-active");
        timeout = 300;
      }

      setTimeout(() => {
        currentProgressBar.replaceWith(newProgressBar);
      }, timeout);

      fireEvent("multistep-form:load", document.documentElement);
    } else {
      if (res.messages) {
        handleMessages(form, validator, res.messages, res.success);
      }
      if (res instanceof Array) {
        handleMessages(form, validator, res, false);
      }
    }
  } catch (error) {
    console.error(error);
    showFormMessages(form, [{ message: "Sorry, there was a problem with your submission" }]);
  } finally {
    currentSection.classList.remove("is-loading");
    toggleSubmit(form);
  }
};

const handlePreviousStepOrLink = async (url) => {
  const currentProgressBar = document.querySelector("[data-multistep-progress-bar]");
  const currentSection = document.querySelector("[data-multistep-content]");
  currentSection.classList.add("is-loading");

  const res = await ajax.get(url).text();

  const range = document.createRange();
  const fragment = range.createContextualFragment(res);
  const newSection = fragment.querySelector("[data-multistep-content]");
  currentSection.replaceWith(newSection);

  if (url) {
    window.history.pushState({ url }, "", url);
  }

  const newProgressBar = fragment.querySelector("[data-multistep-progress-bar]");
  currentProgressBar.replaceWith(newProgressBar);

  fireEvent("multistep-form:load", document.documentElement);
};

const handleFinalStep = async (form, validator) => {
  const formData = new FormData(form);
  const currentSection = document.querySelector("[data-multistep-content]");
  const stepsHeader = document.querySelector("[data-steps-header]");

  currentSection?.classList.add("is-loading");

  try {
    const req = await ajax.post(form.action, { body: formData });
    const contentType = req.headers.get("content-type");
    const res = contentType.includes("application/json") ? await req.json() : await req.text();

    if (res.success && res.completed) {
      const range = document.createRange();
      const fragment = range.createContextualFragment(res.html);
      const newSection = fragment.querySelector("[data-multistep-content]");
      currentSection.replaceWith(newSection);

      if (stepsHeader) {
        stepsHeader.style.display = "none";
      }

      if (res.analytics && window.dataLayer) {
        window.dataLayer.push(res.analytics);
      }

      const completedUrl = form.dataset.completedUrl;
      if (completedUrl) {
        window.history.pushState({ url: completedUrl }, "", completedUrl);
      }

      fireEvent("multistep-form:completed", document.documentElement);
    } else {
      if (res.messages) {
        handleMessages(form, validator, res.messages, res.success);
      } else if (res instanceof Array) {
        handleMessages(form, validator, res, false);
      } else {
        showFormMessages(form, [{ message: "Sorry, there was a problem with your submission" }]);
      }
    }
  } catch (error) {
    console.error(error);
    showFormMessages(form, [{ message: "Sorry, there was a problem with your submission" }]);
  } finally {
    currentSection?.classList.remove("is-loading");
    toggleSubmit(form);
  }
};

export const setupMultistepForm = (form) => {
  const validator = form.dataset.formValidate !== undefined && createValidator(form);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    toggleSubmit(form);

    if (validator) {
      const result = validator.validateForm();
      const firstError = form.querySelector(".has-error");
      if (!result.isValid) {
        toggleSubmit(form);
        if (firstError) {
          scrollToIfNeeded(firstError, 32);
        }
        return;
      }
    }

    if (form.dataset.finalStep !== undefined) {
      handleFinalStep(form, validator);
    } else {
      handleNextStep(form, validator);
    }
  });

  const previousStep = form.querySelector(".previous-step");

  if (previousStep) {
    previousStep.addEventListener("click", (e) => {
      e.preventDefault();
      handlePreviousStepOrLink(e.target.closest("a").href);
    });
  }

  setTimeout(() => {
    tippy("[data-step-label]", {
      theme: "light",
      content: (ref) => {
        return ref.getAttribute("aria-label");
      },
      touch: true,
    });
  }, 400);
};

export const setupHistoryListener = () => {
  window.addEventListener("popstate", (e) => {
    if (e.state?.url) {
      handlePreviousStepOrLink(e.state.url);
    }
  });
};

export const setupStepLinkListener = () => {
  delegate(".progress-step-link, .step-link", "click", (e) => {
    e.preventDefault();

    handlePreviousStepOrLink(e.delegateTarget.href);
  });
};
